import type { ApiResult } from '../types';
import type { PublicPetitionWizardDraft } from '../types/publicPetitionCaseRequest';
import type { PublicPetitionStepsResponse } from '../types/publicPetitionApi';
import type { DocumentChecklistItem } from '../mocks/criminalMasterData';
import {
  isPublicPetitionCategory,
  setPublicPetitionCategoryId,
  type PublicPetitionWizardStep,
} from '../pages/wizard/publicPetition/publicPetitionConstants';
import { DEFAULT_PUBLIC_PETITION_DRAFT } from '../layouts/PublicPetitionRequestLayout';
import { caseApiRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import * as criminalService from './criminalCaseRequests';
import {
  buildSavePublicPetitionPayload,
  caseRequestIdFromResponse,
  mapPublicPetitionStepsToDraft,
  referenceNoFromResponse,
  resolvePublicPetitionDraftResumeStep,
} from './publicPetitionCaseMapper';

export const getFileCategories = criminalService.getFileCategories;

/** Resolve Public Petition category id from master data when available. */
export async function resolvePublicPetitionCategoryId(): Promise<string> {
  const res = await getFileCategories();
  if (!res.success) return '';
  const match = res.data.find((c) => isPublicPetitionCategory(c));
  if (match?.id) setPublicPetitionCategoryId(match.id);
  return match?.id ?? '';
}

export const getSubFileCategories = criminalService.getSubFileCategories;
export const getSubCategoryApplicableAreas = criminalService.getSubCategoryApplicableAreas;
export const getProvinces = criminalService.getProvinces;
export const getDistricts = criminalService.getDistricts;
export const getUrgencyLevels = criminalService.getUrgencyLevels;
export const getDeliveryMethods = criminalService.getDeliveryMethods;
export const getRespondentTypes = criminalService.getRespondentTypes;
export const uploadCaseFile = criminalService.uploadCaseFile;
export const downloadCaseFile = criminalService.downloadCaseFile;
export {
  getCommonCoverNote as getPublicPetitionCoverNote,
  downloadCommonCoverNotePdf as downloadPublicPetitionCoverNotePdf,
} from './coverNoteRequests';

export async function getDocumentChecklist(
  fileCategoryId: string,
  subFileCategoryId: string,
): Promise<ApiResult<DocumentChecklistItem[]>> {
  if (!fileCategoryId) return { success: true, data: [] };
  return criminalService.getDocumentChecklist(fileCategoryId, subFileCategoryId);
}

export type SavePublicPetitionResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

/** CMS-PP-1 — save/update public petition steps 1–5 */
export async function savePublicPetitionRequest(
  draft: PublicPetitionWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: PublicPetitionWizardStep;
    allSteps?: boolean;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SavePublicPetitionResult>> {
  let fileCategoryId = draft.fileCategoryId.trim();
  if (!fileCategoryId) {
    fileCategoryId = await resolvePublicPetitionCategoryId();
  }
  const body = buildSavePublicPetitionPayload(
    fileCategoryId ? { ...draft, fileCategoryId } : draft,
    {
      isDraft: options.isDraft,
      currentStep: options.currentStep,
      allSteps: options.allSteps,
      confirmedCorrect: options.confirmedCorrect,
      isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
    },
  );

  const res = await caseApiRequest<PublicPetitionStepsResponse>('/public-petitions/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || draft.draftRequestId;

  return {
    success: true,
    data: {
      requestId,
      fileReferenceNumber: referenceNoFromResponse(res.data),
    },
  };
}

export type PublicPetitionRequestViewData = {
  draft: PublicPetitionWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step1?: PublicPetitionStepsResponse['step_1'];
  step2?: PublicPetitionStepsResponse['step_2'];
  step3?: PublicPetitionStepsResponse['step_3'];
  step4?: PublicPetitionStepsResponse['step_4'];
  step5?: PublicPetitionStepsResponse['step_5'];
};

/** CMS-PP-2 — full GET payload (wizard resume + view) */
export async function getPublicPetitionRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<PublicPetitionRequestViewData | null>> {
  if (!caseRequestId.trim()) return { success: true, data: null };

  const res = await caseApiRequest<PublicPetitionStepsResponse>(
    caseRequestStepsPath(`/public-petitions/steps/${encodeURIComponent(caseRequestId)}`, options),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const draft = mapPublicPetitionStepsToDraft(res.data, {
    ...DEFAULT_PUBLIC_PETITION_DRAFT,
    draftRequestId: caseRequestIdFromResponse(res.data) || caseRequestId,
  });

  const statusName = (res.data.status_name ?? res.data.status_code ?? '').trim();
  const submittedAt = (res.data.created_at ?? res.data.updated_at ?? res.data.updatedAt ?? '').trim();
  const raw = res.data as { completion_remarks?: string };

  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step1: res.data.step_1,
      step2: res.data.step_2,
      step3: res.data.step_3,
      step4: res.data.step_4,
      step5: res.data.step_5,
    },
  };
}

/** CMS-PP-2 — load saved public petition steps for continue draft / wizard resume */
export async function getPublicPetitionRequestDetails(
  caseRequestId: string,
): Promise<ApiResult<PublicPetitionWizardDraft | null>> {
  const view = await getPublicPetitionRequestViewData(caseRequestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

/** CMS-PP-2 — which wizard step to open when continuing a saved draft */
export async function getPublicPetitionDraftResumeStep(
  caseRequestId: string,
): Promise<ApiResult<{ step: PublicPetitionWizardStep; requestId: string }>> {
  if (!caseRequestId.trim()) return { success: false, message: 'Case request id is missing.' };

  const res = await caseApiRequest<PublicPetitionStepsResponse>(
    `/public-petitions/steps/${encodeURIComponent(caseRequestId)}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || caseRequestId;
  return {
    success: true,
    data: { step: resolvePublicPetitionDraftResumeStep(res.data), requestId },
  };
}

export async function submitPublicPetitionRequest(
  draft: PublicPetitionWizardDraft,
): Promise<ApiResult<SavePublicPetitionResult>> {
  return savePublicPetitionRequest(draft, {
    isDraft: false,
    allSteps: true,
    confirmedCorrect: draft.confirmCorrect,
  });
}

export type { DocumentChecklistItem };
